import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import WineIcon from '@/components/icons/WineIcon';
import { Card, Heading, Body, MonoLabel, Button, InlineMessage } from '@/components/rc';
import { authFetch } from '@/utils/authFetch';
import { FUNCTION_URLS } from '@/config/functionUrls';
import { openStripeCheckout } from '@/utils/stripeCheckout';
import { getPurchaseProvider } from '@/utils/purchaseAvailability';
import { purchasePremium } from '@/utils/playBilling';
import { trackEvent } from '@/config/analytics';

interface UpgradePromptProps {
  variant: 'fullscreen' | 'modal';
  feature: 'remy' | 'bottles';
  onDismiss?: () => void;
}

// `noPurchase` copy is used where the platform offers no way to buy (see
// utils/purchaseAvailability). It describes the tier without telling the user
// to take an action they cannot take here.
const FEATURE_COPY = {
  remy: {
    title: 'Meet Rémy, Your AI Sommelier',
    description:
      'Rémy knows your cellar inside out. Get personalised food pairings, tasting notes, and expert advice — all powered by AI. Upgrade to Rave Cave Reserve to unlock Rémy.',
    noPurchase:
      'Rémy knows your cellar inside out — personalised food pairings, tasting notes, and expert advice, all powered by AI. Rémy is part of Rave Cave Reserve.',
  },
  bottles: {
    title: 'Your Cellar is Full',
    description:
      'Free accounts can store up to 50 bottles. Upgrade to Rave Cave Reserve for unlimited cellar space.',
    noPurchase:
      'Free accounts can store up to 50 bottles. Rave Cave Reserve removes the limit.',
  },
};

const UpgradePrompt: React.FC<UpgradePromptProps> = ({ variant, feature, onDismiss }) => {
  const copy = FEATURE_COPY[feature];
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // null on a platform with no purchase path at all — see
  // utils/purchaseAvailability.
  const provider = getPurchaseProvider();
  const canPurchase = provider !== null;

  useEffect(() => {
    trackEvent('upgrade_prompt_shown', { variant, feature });
  }, [variant, feature]);

  const handleUpgrade = async () => {
    trackEvent('upgrade_initiated', { feature });
    setLoading(true);
    setError(null);

    if (provider === 'play') {
      const outcome = await purchasePremium();
      // On success, isPremium flips once the webhook lands and the Firestore
      // listener picks it up — this component unmounts on its own when the
      // caller re-renders behind !isPremium, so there's nothing to do here
      // beyond clearing the spinner.
      if (outcome.status === 'error') setError(outcome.message);
      setLoading(false);
      return;
    }

    try {
      const res = await authFetch(FUNCTION_URLS.createCheckout, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to start checkout');
      }
      const { url } = await res.json();
      if (url) {
        await openStripeCheckout(url);
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (e: any) {
      setError(e.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  // Stripe leaves the app for a Custom Tab; Play's purchase sheet is an
  // in-process native dialog, so the in-flight label differs.
  const upgradeLabel = loading
    ? (provider === 'play' ? 'Processing...' : 'Redirecting...')
    : null;

  if (variant === 'fullscreen') {
    return (
      <div className="min-h-full flex items-center justify-center p-6">
        <div className="max-w-[440px] text-center flex flex-col items-center">
          <WineIcon size={48} className="text-[var(--rc-accent-pink)]" />
          <div className="h-4" />
          <Heading scale="title" colour="accent-pink" align="centre">{copy.title}</Heading>
          <div className="h-3" />
          <Body className="w-auto text-center">{canPurchase ? copy.description : copy.noPurchase}</Body>
          {canPurchase && (
            <>
              <div className="h-8" />
              <Button
                variantType="Primary"
                label={upgradeLabel ?? 'Upgrade to Rave Cave Reserve'}
                onClick={handleUpgrade}
                disabled={loading}
                className="w-full max-w-[280px]"
              />
              {error && (
                <>
                  <div className="h-3" />
                  <InlineMessage tone="error" message={error} />
                </>
              )}
              <div className="h-3" />
              <MonoLabel size="label" colour="ghost" className="w-auto">$5.99/month — cancel anytime</MonoLabel>
            </>
          )}
          {/* Without the upgrade button this screen has nothing interactive on
              it, so give the user a way off it. */}
          {!canPurchase && onDismiss && (
            <>
              <div className="h-8" />
              <Button
                variantType="Secondary"
                label="Back to Cellar"
                onClick={onDismiss}
                className="w-full max-w-[280px]"
              />
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4"
        onClick={(e) => { if (e.target === e.currentTarget && !loading) onDismiss?.(); }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="w-full max-w-[400px]"
        >
          <Card elevation="raised" padding="standard">
            <Heading scale="heading" colour="accent-pink">{copy.title}</Heading>
            <div className="h-3" />
            <Body className="w-auto">{canPurchase ? copy.description : copy.noPurchase}</Body>
            {error && (
              <>
                <div className="h-3" />
                <InlineMessage tone="error" message={error} />
              </>
            )}
            <div className="h-6" />
            <div className="flex gap-3">
              <Button
                variantType={canPurchase ? 'Secondary' : 'Primary'}
                label={canPurchase ? 'Maybe Later' : 'Got It'}
                onClick={() => onDismiss?.()}
                disabled={loading}
                className="flex-1"
              />
              {canPurchase && (
                <Button variantType="Primary" label={upgradeLabel ?? 'Upgrade'} onClick={handleUpgrade} disabled={loading} className="flex-1" />
              )}
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpgradePrompt;
